import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, Terminal, MessageSquare, Laptop, Apple, Monitor, 
  CheckCircle, Copy, ExternalLink, Zap, Home, FileText, Bell,
  Search, Calculator, Mail, Clock, Shield, Sparkles, AlertTriangle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const OpenClawGuidePage = () => {
  const [copiedStep, setCopiedStep] = useState(null);

  const copyToClipboard = (text, step) => {
    navigator.clipboard.writeText(text);
    setCopiedStep(step);
    toast.success('Commande copiée !');
    setTimeout(() => setCopiedStep(null), 2000);
  };

  const useCases = [
    {
      icon: Search,
      title: "Veille immobilière automatique",
      description: "Surveille les annonces et te prévient quand un bien correspond à tes critères",
      example: "Surveille LeBonCoin pour les T3 à Lyon < 200k€ et préviens-moi sur WhatsApp"
    },
    {
      icon: FileText,
      title: "Analyse de documents",
      description: "Analyse compromis, baux, PV d'AG et identifie les points de vigilance",
      example: "Analyse ce compromis de vente et liste les clauses à négocier"
    },
    {
      icon: Calculator,
      title: "Calculs de rentabilité",
      description: "Calcule rapidement rentabilité, cashflow, TRI sur n'importe quel bien",
      example: "Calcule la rentabilité nette d'un T2 à 150k€ loué 650€/mois"
    },
    {
      icon: Mail,
      title: "Gestion des emails",
      description: "Répond automatiquement aux demandes de location, relance les artisans",
      example: "Réponds aux demandes de visite avec ce template et propose 3 créneaux"
    },
    {
      icon: Bell,
      title: "Rappels et suivi",
      description: "Te rappelle les échéances importantes (assurances, diagnostics, AG)",
      example: "Rappelle-moi 2 mois avant l'expiration du DPE de chaque bien"
    },
    {
      icon: Home,
      title: "Recherche de marché",
      description: "Recherche les prix au m², tendances du marché, données DVF",
      example: "Quels sont les prix au m² dans le 7ème arrondissement de Lyon ?"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="bg-slate-900/80 border-b border-slate-700 sticky top-0 z-10 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <Link to="/dashboard" className="inline-flex items-center text-slate-400 hover:text-white transition-colors">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour au tableau de bord
          </Link>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-red-500 to-orange-500 mb-6">
            <span className="text-4xl">🦞</span>
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">
            OpenClaw - Ton Assistant IA Personnel
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Un assistant IA qui tourne sur ton ordinateur, disponible 24/7 via WhatsApp, 
            Telegram ou iMessage. Gratuit et open-source.
          </p>
          <div className="flex items-center justify-center gap-3 mt-6">
            <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Open Source</Badge>
            <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">Gratuit</Badge>
            <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">Privé (sur ton PC)</Badge>
          </div>
        </div>

        {/* What can it do */}
        <Card className="bg-slate-800/50 border-slate-700 mb-8">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-amber-400" />
              Ce que tu peux faire avec OpenClaw
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              {useCases.map((useCase, idx) => (
                <div key={idx} className="p-4 rounded-lg bg-slate-700/50 border border-slate-600">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-red-500/20 to-orange-500/20 flex items-center justify-center flex-shrink-0">
                      <useCase.icon className="h-5 w-5 text-orange-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">{useCase.title}</h3>
                      <p className="text-sm text-slate-400 mb-2">{useCase.description}</p>
                      <div className="text-xs text-slate-500 bg-slate-800 rounded p-2 font-mono">
                        "{useCase.example}"
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Prerequisites */}
        <Card className="bg-slate-800/50 border-slate-700 mb-8">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Shield className="h-5 w-5 text-blue-400" />
              Ce dont tu as besoin
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-4 gap-4">
              <div className="p-4 rounded-lg bg-slate-700/50 border border-slate-600 text-center">
                <div className="flex justify-center gap-2 mb-3">
                  <Apple className="h-8 w-8 text-slate-300" />
                  <Monitor className="h-8 w-8 text-slate-300" />
                </div>
                <h3 className="font-semibold text-white mb-1">Un ordinateur</h3>
                <p className="text-sm text-slate-400">Mac ou Windows</p>
              </div>
              <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30 text-center">
                <div className="flex justify-center mb-3">
                  <Shield className="h-8 w-8 text-amber-400" />
                </div>
                <h3 className="font-semibold text-amber-400 mb-1">Droits Admin</h3>
                <p className="text-sm text-slate-400">Compte administrateur requis</p>
              </div>
              <div className="p-4 rounded-lg bg-slate-700/50 border border-slate-600 text-center">
                <div className="flex justify-center mb-3">
                  <MessageSquare className="h-8 w-8 text-green-400" />
                </div>
                <h3 className="font-semibold text-white mb-1">WhatsApp ou Telegram</h3>
                <p className="text-sm text-slate-400">Pour communiquer avec l'assistant</p>
              </div>
              <div className="p-4 rounded-lg bg-slate-700/50 border border-slate-600 text-center">
                <div className="flex justify-center mb-3">
                  <Clock className="h-8 w-8 text-amber-400" />
                </div>
                <h3 className="font-semibold text-white mb-1">20 minutes</h3>
                <p className="text-sm text-slate-400">Pour l'installation</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Installation Steps */}
        <Card className="bg-slate-800/50 border-slate-700 mb-8">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Terminal className="h-5 w-5 text-green-400" />
              Installation sur Mac
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Important Notice */}
            <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
              <p className="text-amber-400 font-medium flex items-center gap-2 mb-2">
                <Shield className="h-5 w-5" />
                Important : Droits administrateur requis
              </p>
              <p className="text-sm text-slate-300">
                Tu dois être connecté avec un compte administrateur sur ton Mac. 
                Si ce n'est pas le cas, va dans Préférences Système → Utilisateurs et groupes 
                et coche "Autoriser l'utilisateur à administrer cet ordinateur".
              </p>
            </div>

            {/* Step 1 - Homebrew */}
            <div className="relative pl-8 pb-6 border-l-2 border-slate-600">
              <div className="absolute -left-3 top-0 w-6 h-6 rounded-full bg-green-500 flex items-center justify-center text-white text-sm font-bold">
                1
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Installe Homebrew (gestionnaire de paquets Mac)</h3>
              <p className="text-slate-300 mb-3">Ouvre le <strong className="text-white">Terminal</strong> (Cmd + Espace → "Terminal" → Entrée), puis copie cette commande :</p>
              <div className="bg-slate-900 rounded-lg p-4 font-mono text-sm">
                <div className="flex items-start justify-between gap-2">
                  <code className="text-green-400 break-all">/bin/bash -c "$(curl -fsSL https://raw.githubusercontent.com/Homebrew/install/HEAD/install.sh)"</code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard('/bin/bash -c "$(curl -fsSL https://raw.githubusercontent.com/Homebrew/install/HEAD/install.sh)"', 'brew')}
                    className="text-slate-400 hover:text-white flex-shrink-0"
                  >
                    {copiedStep === 'brew' ? <CheckCircle className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <p className="text-sm text-slate-400 mt-2">
                Le Terminal va te demander ton mot de passe Mac. Tape-le (rien ne s'affiche, c'est normal) puis Entrée.
                Patiente quelques minutes.
              </p>
            </div>

            {/* Step 2 - Add Homebrew to PATH */}
            <div className="relative pl-8 pb-6 border-l-2 border-slate-600">
              <div className="absolute -left-3 top-0 w-6 h-6 rounded-full bg-green-500 flex items-center justify-center text-white text-sm font-bold">
                2
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Configure Homebrew (après installation)</h3>
              <p className="text-slate-300 mb-3">À la fin de l'installation, Homebrew affiche des commandes "Next steps". Copie-colle celles-ci :</p>
              <div className="bg-slate-900 rounded-lg p-4 font-mono text-sm space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <code className="text-green-400 break-all">{"echo 'eval \"$(/opt/homebrew/bin/brew shellenv)\"' >> ~/.zprofile"}</code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard('echo \'eval "$(/opt/homebrew/bin/brew shellenv)"\' >> ~/.zprofile', 'path1')}
                    className="text-slate-400 hover:text-white flex-shrink-0"
                  >
                    {copiedStep === 'path1' ? <CheckCircle className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
                <div className="flex items-start justify-between gap-2">
                  <code className="text-green-400 break-all">{"eval \"$(/opt/homebrew/bin/brew shellenv)\""}</code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard('eval "$(/opt/homebrew/bin/brew shellenv)"', 'path2')}
                    className="text-slate-400 hover:text-white flex-shrink-0"
                  >
                    {copiedStep === 'path2' ? <CheckCircle className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </div>

            {/* Step 3 - Install Node.js */}
            <div className="relative pl-8 pb-6 border-l-2 border-slate-600">
              <div className="absolute -left-3 top-0 w-6 h-6 rounded-full bg-green-500 flex items-center justify-center text-white text-sm font-bold">
                3
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Installe Node.js</h3>
              <div className="bg-slate-900 rounded-lg p-4 font-mono text-sm">
                <div className="flex items-center justify-between">
                  <code className="text-green-400">brew install node</code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard('brew install node', 'node')}
                    className="text-slate-400 hover:text-white"
                  >
                    {copiedStep === 'node' ? <CheckCircle className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </div>

            {/* Step 4 - Install OpenClaw */}
            <div className="relative pl-8 pb-6 border-l-2 border-slate-600">
              <div className="absolute -left-3 top-0 w-6 h-6 rounded-full bg-green-500 flex items-center justify-center text-white text-sm font-bold">
                4
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Installe OpenClaw</h3>
              <div className="bg-slate-900 rounded-lg p-4 font-mono text-sm">
                <div className="flex items-center justify-between">
                  <code className="text-green-400">npm install -g @anthropics/openclaw</code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard('npm install -g @anthropics/openclaw', 'install')}
                    className="text-slate-400 hover:text-white"
                  >
                    {copiedStep === 'install' ? <CheckCircle className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <p className="text-sm text-slate-400 mt-2">
                Si cette commande ne fonctionne pas, essaie la méthode alternative :
              </p>
              <div className="bg-slate-900 rounded-lg p-4 font-mono text-sm mt-2">
                <div className="flex items-center justify-between">
                  <code className="text-green-400">curl -fsSL https://openclaw.ai/install.sh | bash</code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard('curl -fsSL https://openclaw.ai/install.sh | bash', 'install2')}
                    className="text-slate-400 hover:text-white"
                  >
                    {copiedStep === 'install2' ? <CheckCircle className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </div>

            {/* Step 5 - Configure */}
            <div className="relative pl-8">
              <div className="absolute -left-3 top-0 w-6 h-6 rounded-full bg-green-500 flex items-center justify-center text-white text-sm font-bold">
                5
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Lance la configuration</h3>
              <div className="bg-slate-900 rounded-lg p-4 font-mono text-sm">
                <div className="flex items-center justify-between">
                  <code className="text-green-400">openclaw onboard</code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard('openclaw onboard', 'onboard')}
                    className="text-slate-400 hover:text-white"
                  >
                    {copiedStep === 'onboard' ? <CheckCircle className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <p className="text-sm text-slate-400 mt-2">
                Un assistant va te guider pour configurer WhatsApp/Telegram et choisir ton modèle IA.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Troubleshooting */}
        <Card className="bg-red-500/10 border-red-500/30 mb-8">
          <CardHeader>
            <CardTitle className="text-red-400 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              En cas de problème
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 bg-slate-800/50 rounded-lg">
              <p className="text-white font-medium mb-1">"Need sudo access" ou "not an Administrator"</p>
              <p className="text-sm text-slate-300">
                → Va dans Préférences Système → Utilisateurs et groupes → Clique sur le cadenas → 
                Sélectionne ton compte → Coche "Autoriser l'utilisateur à administrer cet ordinateur"
              </p>
            </div>
            <div className="p-3 bg-slate-800/50 rounded-lg">
              <p className="text-white font-medium mb-1">"command not found: openclaw"</p>
              <p className="text-sm text-slate-300">
                → Ferme le Terminal et rouvre-le, puis réessaie. Si ça persiste, 
                tape : <code className="bg-slate-700 px-1 rounded">source ~/.zprofile</code>
              </p>
            </div>
            <div className="p-3 bg-slate-800/50 rounded-lg">
              <p className="text-white font-medium mb-1">"Homebrew not found"</p>
              <p className="text-sm text-slate-300">
                → Reprends à l'étape 1 et assure-toi de bien copier-coller la commande complète.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Tips */}
        <Card className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-500/30 mb-8">
          <CardHeader>
            <CardTitle className="text-amber-400 flex items-center gap-2">
              <Zap className="h-5 w-5" />
              Astuces pour l'immobilier
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 bg-slate-800/50 rounded-lg">
              <p className="text-white font-medium mb-1">Crée des "skills" personnalisés</p>
              <p className="text-sm text-slate-300">
                Demande à OpenClaw : "Crée un skill qui calcule automatiquement la rentabilité d'un bien 
                quand je lui envoie un lien LeBonCoin"
              </p>
            </div>
            <div className="p-3 bg-slate-800/50 rounded-lg">
              <p className="text-white font-medium mb-1">Connecte-le à ton calendrier</p>
              <p className="text-sm text-slate-300">
                Il peut te rappeler les visites, échéances de diagnostics, dates de fin de bail, etc.
              </p>
            </div>
            <div className="p-3 bg-slate-800/50 rounded-lg">
              <p className="text-white font-medium mb-1">Laisse-le tourner en permanence</p>
              <p className="text-sm text-slate-300">
                Pour qu'il soit toujours disponible, laisse ton ordinateur allumé ou utilise un Mac Mini dédié.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Resources */}
        <div className="grid md:grid-cols-2 gap-4 mb-8">
          <a 
            href="https://openclaw.ai/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="block"
          >
            <Card className="bg-slate-800/50 border-slate-700 hover:border-slate-500 transition-colors h-full">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
                  <span className="text-2xl">🦞</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-white">Site officiel OpenClaw</h3>
                  <p className="text-sm text-slate-400">Documentation complète</p>
                </div>
                <ExternalLink className="h-5 w-5 text-slate-400" />
              </CardContent>
            </Card>
          </a>
          <a 
            href="https://docs.openclaw.ai/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="block"
          >
            <Card className="bg-slate-800/50 border-slate-700 hover:border-slate-500 transition-colors h-full">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                  <FileText className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-white">Documentation</h3>
                  <p className="text-sm text-slate-400">Guides et tutoriels détaillés</p>
                </div>
                <ExternalLink className="h-5 w-5 text-slate-400" />
              </CardContent>
            </Card>
          </a>
        </div>

        {/* Help */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-6 text-center">
            <p className="text-slate-300 mb-4">
              Besoin d'aide pour l'installation ? Pose ta question dans le groupe WhatsApp de la communauté !
            </p>
            <Link to="/community">
              <Button className="bg-green-600 hover:bg-green-700">
                <MessageSquare className="h-4 w-4 mr-2" />
                Rejoindre la communauté
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OpenClawGuidePage;
